import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiCheckCircle } from "react-icons/fi";
import AuthLayoutWrapper from "../../components/auth/AuthCardWrapper";
import api from "../../api/axios";

export default function VerifyOtp() {
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [errorMsg, setErrorMsg] = useState("");
  const [toastMsg, setToastMsg] = useState(""); // Toast notification state
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(59);
  const [canResend, setCanResend] = useState(false);

  const navigate = useNavigate();
  const otpRefs = useRef([]);
  const email = sessionStorage.getItem("reset_email") || "";

  // Handle countdown timer
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Helper function to trigger success toasts
  const showSuccessToast = (message) => {
    setToastMsg(message);
    setTimeout(() => {
      setToastMsg("");
    }, 3000);
  };

  // Input Handling with Backspace Navigation
  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return false;
    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    if (element.value !== "" && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  // Step 1 API Call: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length < 6) {
      setErrorMsg("Please enter complete 6-digit OTP");
      return;
    }

    setErrorMsg("");
    setLoading(true);

    try {
      const response = await api.post("/auth/verify-otp", {
        email,
        otp: otpString,
      });

      if (response.status === 200) {
        sessionStorage.setItem("verified_otp", otpString);

        // Show success toast briefly before navigating
        showSuccessToast("OTP Verified Successfully!");
        setTimeout(() => {
          navigate("/auth/reset-password");
        }, 800);
      }
    } catch (error) {
      setErrorMsg(
        error.response?.data?.message ||
          "Invalid or expired OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP API Call
  const handleResendOtp = async () => {
    if (!canResend) return;
    setErrorMsg("");
    setResendLoading(true);

    try {
      await api.post("/auth/send-otp", { email });
      setTimer(59);
      setCanResend(false);
      setOtp(Array(6).fill(""));
      otpRefs.current[0]?.focus();
      showSuccessToast("A new OTP code has been sent!");
    } catch (error) {
      setErrorMsg(
        error.response?.data?.message ||
          "Failed to resend OTP. Try again later."
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <AuthLayoutWrapper>
      {/* Toast Notification Banner */}
      {toastMsg && (
        <div className="bg-emerald-600 absolute left-1/2 top-4 z-50 flex -translate-x-1/2 animate-bounce items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold text-white shadow-lg transition-all">
          <FiCheckCircle className="text-base" />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Header Section */}
      <div className="mb-4 text-center">
        <h3 className="text-3xl font-extrabold text-[#111827]">Enter OTP</h3>
        <p className="mt-1 text-xs font-semibold text-[var( --color-primary)]">
          We sent a 6-digit code to{" "}
          <span className="font-bold text-gray-900">
            {email || "your email"}
          </span>
        </p>
        <button
          type="button"
          onClick={() => navigate("/auth/forgot-password")}
          className="mt-1 text-xs font-semibold text-[#111827] underline hover:text-[#D97706]"
        >
          Change Mail
        </button>
      </div>

      {/* Error Feedback */}
      {errorMsg ? (
        <div className="mb-3 rounded-lg bg-red-50 p-2.5 text-center text-xs font-medium text-red-600">
          {errorMsg}
        </div>
      ) : null}

      <form
        onSubmit={handleVerifyOtp}
        className="flex flex-1 flex-col justify-between gap-4"
      >
        <div className="space-y-4">
          {/* OTP Box inputs */}
          <div className="flex justify-between gap-1.5">
            {otp.map((data, index) => (
              <input
                key={index}
                type="text"
                maxLength="1"
                ref={(el) => (otpRefs.current[index] = el)}
                value={data}
                onChange={(e) => handleOtpChange(e.target, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onFocus={(e) => e.target.select()}
                className="focus:border-black focus:ring-black h-12 w-12 rounded-xl border border-[#374151] text-center text-base font-bold text-[#111827] outline-none focus:ring-1"
              />
            ))}
          </div>

          {/* Timer & Resend Button */}
          <div className="flex items-center justify-end pt-1 text-xs">
            {canResend ? (
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendLoading}
                className="text-xs font-bold text-[#D97706] hover:underline disabled:opacity-50"
              >
                {resendLoading ? "Sending..." : "Resend Code"}
              </button>
            ) : (
              <span className="text-xs font-semibold text-gray-400">
                00:{timer < 10 ? `0${timer}` : timer}
              </span>
            )}
          </div>
        </div>

        {/* Action Button */}
        <button
          type="submit"
          disabled={loading}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1A1A1A] py-3 text-xs font-bold tracking-wider text-[#FFFFFF] transition-all hover:bg-[#11172E] active:scale-[0.98] disabled:opacity-50"
        >
          {loading ? "VERIFYING..." : "VERIFY"}{" "}
          <FiArrowRight className="text-sm" />
        </button>
        <div className="flex items-center justify-center pt-1 text-xs">
          <button
            type="button"
            onClick={() => navigate("/auth/sign-in")}
            className="text-xs font-semibold text-[#374151] hover:underline"
          >
            Back to Log In
          </button>
        </div>
      </form>
    </AuthLayoutWrapper>
  );
}
